import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { ArrowRight, MessageCircle } from 'lucide-react';

const FAQ = () => {
  const faqItems = [
    {
      id: 1,
      question: "How do I browse sequences?",
      answer: "Head to the homepage or use the search bar to explore sequences by song, display type, or creator. You can also filter by platform and rating to find exactly what you need."
    },
    {
      id: 2,
      question: "How do I upload a sequence?",
      answer: "Click \"Upload Sequence\" in the top menu, fill in the song and display info, and attach your file. You can also set a price or make it free."
    },
    {
      id: 3,
      question: "What file formats are supported?",
      answer: "We accept common sequencing formats, including .xseq, .lms, .loredit, and .xml. Make sure your upload matches your chosen platform (e.g., xLights, Light-O-Rama)."
    },
    {
      id: 4,
      question: "Can I sell my sequences?",
      answer: "Yes! You can set your own price—most creators list theirs between $25 and $50. Light Hunt takes care of payment and delivery."
    },
    {
      id: 5,
      question: "Do you offer refunds?",
      answer: "We do not offer refunds for digital products. Please review the sequence details and previews carefully before purchasing."
    },
    {
      id: 6,
      question: "What if I have a technical issue?",
      answer: "If you run into problems, contact us or use the support form on our Contact page. We typically reply within 12–24 hours."
    }
  ];

  return (
    <>
      <Helmet>
        <title>FAQ - Light Hunt</title>
        <meta name="description" content="Frequently asked questions about Light Hunt - learn how to browse sequences, upload your own, supported file formats, selling sequences, and getting technical support." />
        <meta name="keywords" content="FAQ, help, light hunt, sequence upload, file formats, technical support, refunds" />
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <NavBar />
        <main className="grow container mx-auto px-4 pt-24 pb-8 max-w-4xl">
          <div className="space-y-16">
            {/* Hero Section */}
            <div className="text-center space-y-6">
              <h1 className="text-4xl md:text-5xl font-bold bg-linear-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                Light Hunt FAQ
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Find answers to the most common questions about using Light Hunt.
              </p>
            </div>

            {/* Divider */}
            <div className="flex justify-center">
              <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
            </div>

            {/* FAQ Items */}
            <div className="space-y-12">
              {faqItems.map((item, index) => (
                <div key={item.id} className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm">
                      {item.id}
                    </div>
                    <div className="grow">
                      <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                        {item.question}
                      </h2>
                      <p className="text-muted-foreground leading-relaxed text-lg">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                  
                  {/* Divider between items (except last) */}
                  {index < faqItems.length - 1 && (
                    <div className="flex justify-center pt-8">
                      <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Contact CTA Section */}
            <div className="text-center space-y-6 bg-muted/50 rounded-2xl p-8 md:p-12">
              <MessageCircle className="mx-auto h-12 w-12 text-primary" />
              <h2 className="text-2xl md:text-3xl font-bold">Still have questions?</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Can't find what you're looking for? We're here to help! Reach out to us anytime and we'll get back to you as soon as possible.
              </p>
              <Button asChild size="lg" className="rounded-full px-8">
                <Link to="/contact">
                  Reach out anytime
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default FAQ; 